import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Compass, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";

export interface ContextEmptyStateProps {
  projectId: string;
  hasRepo: boolean;
  canEdit: boolean;
  onStartManualConfig: () => void;
}

const SETUP_PROMPT =
  "/iamu-setup Inspect this repository and configure the domain context, glossary, untestable boundaries, and initial personas in iamu/, then sync to the project.";

export function ContextEmptyState({
  projectId,
  hasRepo,
  canEdit,
  onStartManualConfig,
}: ContextEmptyStateProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(SETUP_PROMPT);
      setCopied(true);
      toast.success("Copied /iamu-setup prompt for your coding agent");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy prompt to clipboard");
    }
  };

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-start gap-4 py-8">
        <div className="flex items-center gap-2 text-primary">
          <Compass className="h-5 w-5" />
          <span className="font-semibold text-sm text-foreground">
            Configure domain context
          </span>
        </div>
        <p className="text-sm text-muted-foreground max-w-xl">
          Teach Iamu about your domain to ground all walkthroughs in your real
          user workflows, stakeholder roles, terminology, and system scope.
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {!hasRepo && (
            <Button asChild size="sm" variant="ghost">
              <Link to={`/projects/${projectId}/settings`}>
                Connect repository
              </Link>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleCopyPrompt}>
            {copied ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <Copy className="mr-2 h-4 w-4" />
            )}
            {copied ? "Copied prompt" : "Copy /iamu-setup prompt"}
          </Button>
          {canEdit && (
            <Button size="sm" onClick={onStartManualConfig}>
              <Pencil className="mr-2 h-4 w-4" />
              Configure manually
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
